import React, { useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { Instagram, Inbox, MessageCircle, Image, Settings, Zap } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { bumpUnreadDMs, bumpUnreadComments, clearUnreadDMs, clearUnreadComments } from '../../redux/instagramSlice';

const tabs = [
  { name: 'Direct Messages', to: '/dashboard/instagram/messages', icon: Inbox, unreadKey: 'unreadDMs' },
  { name: 'Comments', to: '/dashboard/instagram/comments', icon: MessageCircle, unreadKey: 'unreadComments' },
  { name: 'Content', to: '/dashboard/instagram/content', icon: Image },
  { name: 'Automation Rule', to: '/dashboard/instagram/automation', icon: Zap },
  { name: 'Settings', to: '/dashboard/instagram/settings', icon: Settings },
];

export default function InstagramDashboardLayout() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { unreadDMs, unreadComments } = useSelector((s) => s.instagram);
  const unreadByKey = { unreadDMs, unreadComments };

  // The socket connection below is created once and lives for as long as the
  // user stays anywhere in the Instagram Workspace (all 4 tabs share this
  // layout) — its handlers close over `pathnameRef`, not `location` directly,
  // so they see which tab is CURRENTLY active without needing to tear down
  // and reconnect the socket on every tab switch.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);

  // Stays connected across all 4 tabs, unlike InboxPage's own /inbox socket
  // which disconnects the moment you leave it — that's why switching to
  // Content previously meant missing new DM/comment activity entirely until
  // you switched back.
  useEffect(() => {
    const token = localStorage.getItem('token');
    const socket = io('/inbox', { auth: { token }, transports: ['websocket'] });

    socket.on('inbox:new_message', ({ message }) => {
      if (message?.channel === 'instagram' && message?.direction === 'inbound' && !pathnameRef.current.endsWith('/messages')) {
        dispatch(bumpUnreadDMs());
      }
    });
    socket.on('instagram:new_comment', () => {
      if (!pathnameRef.current.endsWith('/comments')) {
        dispatch(bumpUnreadComments());
      }
    });

    return () => socket.disconnect();
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname.endsWith('/messages')) dispatch(clearUnreadDMs());
    if (location.pathname.endsWith('/comments')) dispatch(clearUnreadComments());
  }, [location.pathname, dispatch]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 md:px-6 pt-4 md:pt-6 bg-white border-b border-[#D8DDE6]">
        <div className="mb-4">
          <PageHeader
            title="Instagram Workspace"
            subtitle="Manage Instagram DMs, comments, and content publishing — connected to your CRM."
            icon={Instagram}
          />
        </div>
        <nav className="flex space-x-1 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.to;
            const unread = tab.unreadKey ? unreadByKey[tab.unreadKey] : 0;
            return (
              <NavLink
                key={tab.name}
                to={tab.to}
                className={`relative flex shrink-0 items-center gap-2 py-3 px-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  isActive
                    ? 'border-[#E1306C] text-[#E1306C]'
                    : 'border-transparent text-[#5C5C5C] hover:text-[#2E2E2E] hover:border-[#E5E5E5]'
                }`}
              >
                <span className="relative">
                  <Icon size={16} />
                  {unread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-[#E1306C] text-white text-[9px] font-bold flex items-center justify-center leading-none">
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </span>
                {tab.name}
              </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6 bg-[#F3F3F3] flex flex-col">
        <Outlet />
      </div>
    </div>
  );
}
